import {useEffect, useRef, useState} from 'react';
import {ACCOUNT_ID} from '../../api/account';
import {generateLyrics, generateMusic} from '../../api/music';
import {trackEvent} from '../../utils/analytics';
import {showError} from '../../utils/notify';
import {MUSIC_OCCASIONS, MUSIC_STYLES, MUSIC_VOICES} from '../../config/constants';
import MusicPlayer from './MusicPlayer';

const trimLyricsToLimit = (text, limit = 500) => {
    if (!text || text.length <= limit) return text;
    const blocks = text.split('\n\n');
    while (blocks.length > 1 && blocks.join('\n\n').length > limit) {
        blocks.pop();
    }
    return blocks.join('\n\n');
};

const STEPS = {
    OCCASION: 'occasion',
    STYLE: 'style',
    DETAILS: 'details',
    LYRICS: 'lyrics',
    GENERATING: 'generating',
};

const STORAGE_KEY = 'celebs_music_flow';

function loadSavedState() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const saved = JSON.parse(raw);
        // Rehydrate occasion/style objects from constants by id
        if (saved.selectedOccasion) {
            const found = MUSIC_OCCASIONS.find(o => o.id === saved.selectedOccasion);
            if (found) {
                saved.selectedOccasion = found;
            } else {
                saved.selectedOccasion = {id: saved.selectedOccasion, name: saved.selectedOccasionName || saved.selectedOccasion, icon: '✨'};
            }
        }
        if (saved.selectedStyleIds) {
            saved.selectedStyles = saved.selectedStyleIds
                .map(id => MUSIC_STYLES.find(s => s.id === id))
                .filter(Boolean);
        }
        if (saved.selectedVoice) {
            saved.selectedVoice = MUSIC_VOICES.find(v => v.id === saved.selectedVoice) || MUSIC_VOICES[2];
        }
        if (saved.generations) {
            saved.generations = saved.generations.map(g => ({
                ...g,
                style: MUSIC_STYLES.find(s => s.id === g.styleId) || g.style,
            }));
        }
        return saved;
    } catch {
        return null;
    }
}

function saveState(state) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
            step: state.step,
            selectedOccasion: state.selectedOccasion?.id || null,
            selectedOccasionName: state.selectedOccasion?.name || null,
            selectedStyleIds: state.selectedStyles.map(s => s.id),
            selectedVoice: state.selectedVoice?.id || null,
            details: state.details,
            lyrics: state.lyrics,
            generations: state.generations.map(g => ({
                id: g.id,
                styleId: g.style.id,
                status: g.status,
                fullAudioUrl: g.fullAudioUrl,
            })),
        }));
    } catch { /* quota exceeded — ignore */
    }
}

export default function MusicCard({musicCredits, musicHistory = [], onNoCredits}) {
    const saved = useRef(loadSavedState()).current;

    const [step, setStep] = useState(saved?.step || STEPS.OCCASION);
    const [selectedStyles, setSelectedStyles] = useState(saved?.selectedStyles || []);
    const [selectedOccasion, setSelectedOccasion] = useState(saved?.selectedOccasion || null);
    const [customOccasionText, setCustomOccasionText] = useState('');
    const [selectedVoice, setSelectedVoice] = useState(saved?.selectedVoice || MUSIC_VOICES[2]);
    const [details, setDetails] = useState(saved?.details || '');
    const [lyrics, setLyrics] = useState(saved?.lyrics || '');
    const [isGeneratingLyrics, setIsGeneratingLyrics] = useState(false);
    const [generations, setGenerations] = useState(saved?.generations || []);
    const [showRegenerateModal, setShowRegenerateModal] = useState(false);
    const [regenerateInstruction, setRegenerateInstruction] = useState('');
    const [playingStyleId, setPlayingStyleId] = useState(null);
    const sampleAudioRef = useRef(null);

    const creditsNeeded = selectedStyles.length;
    const occasionDisplayName = selectedOccasion?.name;

    // Persist state to localStorage on every change
    useEffect(() => {
        saveState({
            step,
            selectedStyles,
            selectedOccasion,
            selectedVoice,
            details,
            lyrics,
            generations
        });
    }, [step, selectedStyles, selectedOccasion, selectedVoice, details, lyrics, generations]);

    // Sync generation statuses from account-status polling
    useEffect(() => {
        if (!musicHistory.length || !generations.length) return;
        setGenerations(prev => {
            let changed = false;
            const updated = prev.map(gen => {
                if (!gen.id || gen.status === 'completed' || gen.status === 'failed' || gen.status === 'cancelled') return gen;
                const remote = musicHistory.find(g => g.id === gen.id);
                if (!remote) return gen;
                if (remote.status === 'completed' && remote.full_audio_url) {
                    changed = true;
                    return {...gen, status: 'completed', fullAudioUrl: remote.full_audio_url};
                }
                if (remote.status === 'failed' || remote.status === 'cancelled') {
                    changed = true;
                    return {...gen, status: remote.status};
                }
                return gen;
            });
            return changed ? updated : prev;
        });
    }, [musicHistory, generations.length]);

    const handleOccasionSelect = (occasion) => {
        if (occasion.id === 'other') {
            setSelectedOccasion(occasion);
        } else {
            setSelectedOccasion(occasion);
            setCustomOccasionText('');
            setStep(STEPS.STYLE);
        }
    };

    const handleCustomOccasionConfirm = () => {
        if (!customOccasionText.trim()) return;
        setSelectedOccasion({id: customOccasionText.trim(), name: customOccasionText.trim(), icon: '✨'});
        setCustomOccasionText('');
        setStep(STEPS.STYLE);
    };

    const handleStyleToggle = (style) => {
        setSelectedStyles(prev => {
            const exists = prev.find(s => s.id === style.id);
            if (exists) return prev.filter(s => s.id !== style.id);
            return [...prev, style];
        });
    };

    const handlePlaySample = (e, style) => {
        e.stopPropagation();
        if (playingStyleId === style.id) {
            sampleAudioRef.current?.pause();
            setPlayingStyleId(null);
            return;
        }
        if (sampleAudioRef.current) {
            sampleAudioRef.current.pause();
        }
        const audio = new Audio(style.sampleUrl);
        sampleAudioRef.current = audio;
        audio.play().catch(() => setPlayingStyleId(null));
        setPlayingStyleId(style.id);
        audio.onended = () => setPlayingStyleId(null);
    };

    const handleStylesConfirm = () => {
        if (selectedStyles.length === 0) return;
        if (lyrics.trim()) {
            setStep(STEPS.LYRICS);
        } else {
            setStep(STEPS.DETAILS);
        }
    };

    const handleDetailsSubmit = async () => {
        if (!details.trim()) return;
        setIsGeneratingLyrics(true);

        try {
            const data = await generateLyrics({
                occasion: selectedOccasion.id,
                details: details,
                musicStyle: selectedStyles[0].id,
            });
            setLyrics(trimLyricsToLimit(data.lyrics || ''));
            trackEvent('generate_lyrics');
            setStep(STEPS.LYRICS);
        } catch (error) {
            console.error('Lyrics generation error:', error);
            showError('Не удалось сгенерировать текст. Попробуйте ещё раз.');
        } finally {
            setIsGeneratingLyrics(false);
        }
    };

    const handleRegenerateLyrics = async (instruction) => {
        setShowRegenerateModal(false);
        setIsGeneratingLyrics(true);
        try {
            const data = await generateLyrics({
                occasion: selectedOccasion.id,
                details: details,
                musicStyle: selectedStyles[0].id,
                currentLyrics: lyrics,
                instruction: instruction || undefined,
            });
            setLyrics(trimLyricsToLimit(data.lyrics || ''));
        } catch (error) {
            console.error('Lyrics regeneration error:', error);
        } finally {
            setIsGeneratingLyrics(false);
            setRegenerateInstruction('');
        }
    };

    const handleGenerate = async () => {
        if (musicCredits < creditsNeeded) {
            onNoCredits?.();
            return;
        }

        const initialGenerations = selectedStyles.map(style => ({
            id: null,
            style,
            status: 'processing',
            fullAudioUrl: null,
        }));
        setGenerations(initialGenerations);
        setStep(STEPS.GENERATING);

        try {
            const data = await generateMusic({
                accountId: ACCOUNT_ID,
                musicStyles: selectedStyles.map(s => s.id),
                occasion: selectedOccasion.id,
                lyrics: lyrics,
                details: details,
                voice: selectedVoice.id,
            });

            setGenerations(prev => {
                const updated = [...prev];
                data.results.forEach((result, idx) => {
                    if (idx < updated.length) {
                        updated[idx] = {
                            ...updated[idx],
                            id: result.generation_id,
                            status: result.success ? 'processing' : (result.status === 'no_credits' ? 'no_credits' : 'failed'),
                        };
                    }
                });
                for (let i = data.results.length; i < updated.length; i++) {
                    updated[i] = {...updated[i], status: 'skipped'};
                }
                return updated;
            });

            if (data.results.some(r => r.status === 'no_credits')) {
                onNoCredits?.();
            }
        } catch (error) {
            console.error('Generation error:', error);
            setGenerations(prev => prev.map(g => ({...g, status: 'failed'})));
        }
    };

    const handleStartOver = () => {
        setStep(STEPS.OCCASION);
        setSelectedStyles([]);
        setSelectedOccasion(null);
        setCustomOccasionText('');
        setSelectedVoice(MUSIC_VOICES[2]);
        setDetails('');
        setLyrics('');
        setGenerations([]);
        localStorage.removeItem(STORAGE_KEY);
    };

    return (
        <div className="max-w-[1110px] mx-auto px-4 py-10">
            <div className="max-w-2xl mx-auto">
                {/* Step 1: Select Occasion */}
                {step === STEPS.OCCASION && (
                    <div>
                        <p className="text-[28px] font-medium text-brand-dark mb-6">Выберите повод</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {MUSIC_OCCASIONS.map(occasion => (
                                <button
                                    key={occasion.id}
                                    onClick={() => handleOccasionSelect(occasion)}
                                    className={`p-5 rounded-[16px] transition text-center border-2 ${
                                        selectedOccasion?.id === occasion.id
                                            ? 'border-brand-red bg-red-50'
                                            : 'border-transparent bg-brand-gray hover:bg-gray-200'
                                    }`}
                                >
                                    <span className="text-3xl mb-2 block">{occasion.icon}</span>
                                    <span className="font-medium text-brand-dark">{occasion.name}</span>
                                </button>
                            ))}
                        </div>

                        {selectedOccasion?.id === 'other' && (
                            <div className="mt-4">
                                <input
                                    type="text"
                                    value={customOccasionText}
                                    onChange={(e) => setCustomOccasionText(e.target.value)}
                                    placeholder="Напишите свой повод..."
                                    className="w-full p-4 rounded-[16px] border border-gray-200 focus:border-brand-red focus:outline-none text-brand-dark"
                                />
                                <button
                                    onClick={handleCustomOccasionConfirm}
                                    disabled={!customOccasionText.trim()}
                                    className="mt-3 w-full py-4 rounded-2xl bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-base font-semibold hover:opacity-95 transition disabled:opacity-50"
                                >
                                    Далее
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Step 2: Select Styles (multi) */}
                {step === STEPS.STYLE && (
                    <div>
                        <p className="text-[28px] font-medium text-brand-dark mb-6">Выберите стиль музыки</p>
                        <p className="text-sm text-brand-dark/40 mb-4">
                            Можно выбрать несколько — на каждый стиль создастся отдельная песня
                        </p>
                        <div className="flex items-center gap-2 mb-6">
                            <span className="text-2xl">{selectedOccasion.icon}</span>
                            <span className="font-medium text-brand-dark">{occasionDisplayName}</span>
                            <button
                                onClick={handleStartOver}
                                className="text-sm text-brand-dark/60 hover:text-brand-dark transition"
                            >
                                Начать заново
                            </button>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {MUSIC_STYLES.map(style => {
                                const isSelected = selectedStyles.some(s => s.id === style.id);
                                const isPlaying = playingStyleId === style.id;
                                return (
                                    <button
                                        key={style.id}
                                        onClick={() => handleStyleToggle(style)}
                                        className={`relative p-5 rounded-[16px] transition text-center border-2 ${
                                            isSelected
                                                ? 'border-brand-red bg-red-50'
                                                : 'border-transparent bg-brand-gray hover:bg-gray-200'
                                        }`}
                                    >
                                        <span className="text-3xl mb-2 block">{style.icon}</span>
                                        <span className="font-medium text-brand-dark">{style.name}</span>
                                        {style.sampleUrl && (
                                            <span
                                                onClick={(e) => handlePlaySample(e, style)}
                                                className={`absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full transition text-sm ${
                                                    isPlaying
                                                        ? 'bg-brand-red text-white'
                                                        : 'bg-black/5 text-brand-dark/50 hover:bg-black/10 hover:text-brand-dark'
                                                }`}
                                            >
                                                {isPlaying ? '⏸' : '▶'}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {selectedStyles.length > 0 && (
                            <>
                                <p className="text-sm text-brand-dark/40 mt-6 mb-3">Тип голоса</p>
                                <div className="grid grid-cols-3 gap-3">
                                    {MUSIC_VOICES.map(voice => {
                                        const isSelected = selectedVoice?.id === voice.id;
                                        return (
                                            <button
                                                key={voice.id}
                                                onClick={() => setSelectedVoice(voice)}
                                                className={`p-3 rounded-[16px] transition text-center border-2 ${
                                                    isSelected
                                                        ? 'border-brand-red bg-red-50'
                                                        : 'border-transparent bg-brand-gray hover:bg-gray-200'
                                                }`}
                                            >
                                                <span className="text-2xl mb-1 block">{voice.icon}</span>
                                                <span
                                                    className="text-sm font-medium text-brand-dark">{voice.name}</span>
                                            </button>
                                        );
                                    })}
                                </div>

                                <div className="mt-4 text-sm text-brand-dark/60 text-center">
                                    {selectedStyles.length === 1
                                        ? `1 песня — 1 кредит`
                                        : `${selectedStyles.length} песни — ${selectedStyles.length} кредитов`
                                    }
                                    {' '}(у вас {musicCredits})
                                </div>
                            </>
                        )}

                        <button
                            onClick={handleStylesConfirm}
                            disabled={selectedStyles.length === 0}
                            className="mt-4 w-full py-4 rounded-2xl bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-base font-semibold hover:opacity-95 transition disabled:opacity-50"
                        >
                            Далее
                        </button>
                    </div>
                )}

                {/* Step 3: Enter Details */}
                {step === STEPS.DETAILS && (
                    <div>
                        <div className="flex items-center gap-4 mb-6 text-sm">
                            <button
                                onClick={() => setStep(STEPS.OCCASION)}
                                className="text-brand-dark/60 hover:text-brand-dark transition flex items-center gap-1"
                            >
                                {selectedOccasion.icon} {occasionDisplayName}
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                     className="opacity-40">
                                    <path d="M11 4H4v14a2 2 0 002 2h12a2 2 0 002-2v-7"/>
                                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                </svg>
                            </button>
                            <button
                                onClick={() => setStep(STEPS.STYLE)}
                                className="text-brand-dark/60 hover:text-brand-dark transition flex items-center gap-1"
                            >
                                {selectedStyles.map(s => s.icon).join(' ')} {selectedVoice?.icon}
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                     className="opacity-40">
                                    <path d="M11 4H4v14a2 2 0 002 2h12a2 2 0 002-2v-7"/>
                                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                </svg>
                            </button>
                            <button
                                onClick={handleStartOver}
                                className="text-sm text-brand-dark/60 hover:text-brand-dark transition"
                            >
                                Начать заново
                            </button>
                        </div>
                        <p className="text-lg font-medium text-brand-dark/60 mb-4">
                            Расскажите детали для песни
                        </p>
                        <textarea
                            value={details}
                            onChange={(e) => setDetails(e.target.value)}
                            placeholder="Например: Маша, 30 лет, любит путешествия и кофе..."
                            className="w-full p-4 rounded-[16px] border border-gray-200 focus:border-brand-red focus:outline-none resize-none h-32 text-brand-dark"
                        />
                        <button
                            onClick={handleDetailsSubmit}
                            disabled={!details.trim() || isGeneratingLyrics}
                            className="mt-4 w-full py-4 rounded-2xl bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-base font-semibold hover:opacity-95 transition disabled:opacity-50"
                        >
                            {isGeneratingLyrics ? 'Генерируем текст...' : 'Сгенерировать текст'}
                        </button>
                    </div>
                )}

                {/* Step 4: Review Lyrics */}
                {step === STEPS.LYRICS && (
                    <div>
                        <div className="flex items-center gap-4 mb-6 text-sm">
                            <button
                                onClick={() => setStep(STEPS.OCCASION)}
                                className="text-brand-dark/60 hover:text-brand-dark transition flex items-center gap-1"
                            >
                                {selectedOccasion.icon} {occasionDisplayName}
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                     className="opacity-40">
                                    <path d="M11 4H4v14a2 2 0 002 2h12a2 2 0 002-2v-7"/>
                                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                </svg>
                            </button>
                            <button
                                onClick={() => setStep(STEPS.STYLE)}
                                className="text-brand-dark/60 hover:text-brand-dark transition flex items-center gap-1"
                            >
                                {selectedStyles.map(s => s.icon).join(' ')} {selectedVoice?.icon}
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                     className="opacity-40">
                                    <path d="M11 4H4v14a2 2 0 002 2h12a2 2 0 002-2v-7"/>
                                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                </svg>
                            </button>
                            <button
                                onClick={handleStartOver}
                                className="text-sm text-brand-dark/60 hover:text-brand-dark transition"
                            >
                                Начать заново
                            </button>
                        </div>
                        <p className="text-lg font-medium text-brand-dark/60 mb-4">
                            Текст песни — можно отредактировать
                        </p>
                        <textarea
                            value={lyrics}
                            onChange={(e) => {
                                if (e.target.value.length <= 500) setLyrics(e.target.value);
                            }}
                            maxLength={500}
                            className="w-full p-4 rounded-[16px] border border-gray-200 focus:border-brand-red focus:outline-none resize-none h-48 text-brand-dark font-mono text-sm leading-relaxed"
                        />
                        <p className={`text-right text-sm mt-1 ${lyrics.length >= 500 ? 'text-red-500' : 'text-brand-dark/40'}`}>
                            {lyrics.length}/500
                        </p>
                        <div className="flex gap-3 mt-3">
                            <button
                                onClick={() => setShowRegenerateModal(true)}
                                disabled={isGeneratingLyrics}
                                className="flex-1 py-4 rounded-2xl bg-brand-gray text-brand-dark text-base font-semibold hover:bg-gray-200 transition disabled:opacity-50"
                            >
                                {isGeneratingLyrics ? 'Генерируем...' : 'Заново'}
                            </button>
                            <button
                                onClick={handleGenerate}
                                disabled={!lyrics.trim()}
                                className="flex-1 py-4 rounded-2xl bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-base font-semibold hover:opacity-95 transition disabled:opacity-50"
                            >
                                {musicCredits >= creditsNeeded
                                    ? `Создать ${creditsNeeded > 1 ? `${creditsNeeded} песни` : 'песню'} (${creditsNeeded} кр.)`
                                    : `Создать (нужно ${creditsNeeded} кр.)`
                                }
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 5: Generating & Results */}
                {step === STEPS.GENERATING && (
                    <div>
                        <div className="flex items-center gap-4 mb-6 text-sm text-brand-dark/60">
                            <span>{selectedOccasion.icon} {occasionDisplayName}</span>
                            <span>{selectedStyles.map(s => s.icon).join(' ')} {selectedVoice?.icon}</span>
                            <button
                                onClick={handleStartOver}
                                className="text-sm text-brand-dark/60 hover:text-brand-dark transition"
                            >
                                Начать заново
                            </button>
                        </div>

                        <div className="space-y-4">
                            {generations.map((gen, idx) => (
                                <div key={idx} className="bg-brand-gray rounded-[20px] p-6">
                                    <div className="flex items-center gap-2 mb-3">
                                        <span className="text-xl">{gen.style.icon}</span>
                                        <span className="font-medium text-brand-dark">{gen.style.name}</span>
                                    </div>

                                    {gen.status === 'processing' && (
                                        <div className="flex items-center gap-3">
                                            <div
                                                className="w-5 h-5 border-2 border-gray-200 border-t-brand-red rounded-full animate-spin shrink-0"></div>
                                            <p className="text-sm text-brand-dark/60">Генерируем...</p>
                                        </div>
                                    )}

                                    {gen.status === 'failed' && (
                                        <p className="text-sm text-red-600">Не удалось сгенерировать. Кредит
                                            возвращён.</p>
                                    )}

                                    {gen.status === 'cancelled' && (
                                        <p className="text-sm text-red-600">Генерация песни отменена. Кредит
                                            возвращён.</p>
                                    )}

                                    {gen.status === 'no_credits' && (
                                        <p className="text-sm text-red-600">Недостаточно кредитов</p>
                                    )}

                                    {gen.status === 'skipped' && (
                                        <p className="text-sm text-brand-dark/40">Пропущено</p>
                                    )}

                                    {gen.status === 'completed' && gen.fullAudioUrl && (
                                        <MusicPlayer src={gen.fullAudioUrl} downloadable/>
                                    )}
                                </div>
                            ))}
                        </div>

                        {generations.every(g => ['completed', 'failed', 'no_credits', 'skipped'].includes(g.status)) && (
                            <button
                                onClick={handleStartOver}
                                className="mt-6 w-full py-4 rounded-2xl bg-brand-gray text-brand-dark text-base font-semibold hover:bg-gray-200 transition"
                            >
                                Создать ещё
                            </button>
                        )}

                        <details className="mt-4 bg-brand-gray rounded-[16px] p-4">
                            <summary className="font-medium text-brand-dark cursor-pointer">Текст песни</summary>
                            <pre
                                className="mt-3 text-sm text-brand-dark/70 whitespace-pre-wrap font-sans">{lyrics}</pre>
                        </details>
                    </div>
                )}

            </div>

            {showRegenerateModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
                    onClick={() => setShowRegenerateModal(false)}
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <p className="text-lg font-semibold text-brand-dark">Сгенерировать заново</p>
                            <button
                                onClick={() => setShowRegenerateModal(false)}
                                className="text-brand-dark/40 hover:text-brand-dark text-2xl leading-none"
                            >
                                &times;
                            </button>
                        </div>
                        <p className="text-sm text-brand-dark/60 mb-2">Ваш промпт</p>
                        <textarea
                            value={details}
                            onChange={(e) => setDetails(e.target.value)}
                            className="w-full p-3 rounded-[12px] border border-gray-200 focus:border-brand-red focus:outline-none resize-none h-24 text-brand-dark text-sm"
                        />
                        <p className="text-sm text-brand-dark/60 mb-2 mt-3">Что изменить в тексте?</p>
                        <textarea
                            value={regenerateInstruction}
                            onChange={(e) => setRegenerateInstruction(e.target.value)}
                            placeholder="Например: сделай припев более энергичным, добавь имя Саша..."
                            className="w-full p-3 rounded-[12px] border border-gray-200 focus:border-brand-red focus:outline-none resize-none h-20 text-brand-dark text-sm"
                        />
                        <button
                            onClick={() => handleRegenerateLyrics(regenerateInstruction)}
                            className="mt-4 w-full py-3 rounded-2xl bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-base font-semibold hover:opacity-95 transition"
                        >
                            Сгенерировать
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
